import React from 'react';
import { LegalLayout } from './LegalLayout';
import { useLanguage } from '@/app/contexts/LanguageContext';

interface AGBProps {
  onBack: () => void;
}

export function AGB({ onBack }: AGBProps) {
  const { language } = useLanguage();

  if (language === 'en') {
    return (
      <LegalLayout title="General Terms and Conditions (GTC)" onBack={onBack}>
        <div className="space-y-8 text-slate-700">
          <p className="text-sm text-slate-500 mb-6">(Updated: 05.12.2017)</p>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">1. General Principles / Scope of Application</h3>
            <p className="mb-2">
              For all legal transactions between the Client and Build Informed GmbH (hereinafter referred to as the Contractor), these General Terms and Conditions apply exclusively. The version valid at the time of the conclusion of the contract is decisive.
            </p>
            <p className="mb-2">
              These General Terms and Conditions also apply to all future contractual relationships, even if they are not explicitly referred to in additional contracts.
            </p>
            <p className="mb-2">
              Conflicting General Terms and Conditions of the Client are invalid unless explicitly recognized by the Contractor in writing.
            </p>
            <p>
              In the event that individual provisions of these General Terms and Conditions should be and/or become invalid, this shall not affect the validity of the remaining provisions and the contracts concluded on their basis. The invalid provision is to be replaced by a valid provision that comes closest to its meaning and economic purpose.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">2. Scope of the Order / Representation</h3>
            <p className="mb-2">
              The scope of a specific order is contractually agreed in each individual case.
            </p>
            <p className="mb-2">
              The Contractor is entitled to have the tasks incumbent upon him performed wholly or partially by third parties. The payment of the third party is made exclusively by the Contractor himself. No direct contractual relationship of any kind arises between the third party and the Client.
            </p>
            <p>
              The Client undertakes not to enter into any business relationship of any kind with persons or companies used by the Contractor to fulfill his contractual obligations during and up to three years after the termination of this contractual relationship. In particular, the Client will not commission these persons and companies with such or similar services that the Contractor also offers.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">3. Duty of Disclosure / Declaration of Completeness</h3>
            <p className="mb-2">
              The Client ensures that the organizational framework conditions allow for work that is as undisturbed as possible and conducive to the rapid progress of the work process at his place of business when fulfilling the order.
            </p>
            <p className="mb-2">
              The Client will also comprehensively inform the Contractor about previously conducted and/or ongoing consultations – also in other specialist areas.
            </p>
            <p className="mb-2">
              The Client ensures that all documents necessary for the fulfillment and execution of the consulting order are presented to the Contractor in a timely manner, even without his special request, and that he is informed of all processes and circumstances that are of importance for the execution of the consulting order. This also applies to all documents, processes, and circumstances that only become known during the consultant's activity.
            </p>
            <p>
              The Client ensures that his employees and the legally provided and potentially established employee representation (works council) are informed of this before the Contractor begins his activity.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">4. Safeguarding Independence</h3>
            <p className="mb-2">
              The contracting partners undertake to mutual loyalty.
            </p>
            <p>
              The contracting partners mutually undertake to take all precautions that are suitable to prevent the endangerment of the independence of the commissioned third parties and employees of the Contractor. This applies in particular to offers from the Client for employment or the acceptance of orders on his own account.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">5. Reporting / Duty to Report</h3>
            <p>
              The Contractor is free from instructions in the production of the agreed work, acts at his own discretion and on his own responsibility. He is not bound to any specific place of work or any specific working hours.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">6. Protection of Intellectual Property</h3>
            <p className="mb-2">
              The copyrights to the works created by the Contractor and his employees and commissioned third parties (in particular offers, reports, analyses, expert opinions, organizational plans, programs, service descriptions, drafts, calculations, drawings, data carriers, etc.) remain with the Contractor. They may be used by the Client during and after termination of the contractual relationship exclusively for purposes covered by the contract. The Client is therefore not entitled to reproduce and/or distribute the work(s) without the express consent of the Contractor. Under no circumstances does an unauthorized reproduction/distribution of the work give rise to any liability of the Contractor – in particular, for example, for the correctness of the work – towards third parties.
            </p>
            <p>
              The Client's violation of these provisions entitles the Contractor to immediate premature termination of the contractual relationship and to assert other legal claims, in particular for injunctive relief and/or damages.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">7. Liability / Damages</h3>
            <p className="mb-2">
              The Contractor is liable to the Client for damages – except for personal injury – only in the event of gross negligence (intent or gross negligence). This applies mutatis mutandis also to damages attributable to third parties consulted by the Contractor.
            </p>
            <p className="mb-2">
              Claims for damages by the Client can only be asserted in court within six months from knowledge of the damage and the damaging party, but at the latest within three years after the event giving rise to the claim.
            </p>
            <p className="mb-2">
              The Client must provide proof in each case that the damage is due to the fault of the Contractor.
            </p>
            <p>
              If the Contractor performs the work with the help of third parties and warranty and/or liability claims arise against these third parties in this context, the Contractor assigns these claims to the Client. In this case, the Client will primarily hold these third parties liable.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">8. Confidentiality / Data Protection</h3>
            <p className="mb-2">
              The Contractor undertakes to maintain silence about business and trade secrets of the Client.
            </p>
            <p className="mb-2">
              The Contractor is released from the duty of confidentiality towards any assistants and deputies he uses. However, he must fully transfer the duty of confidentiality to them and is liable for their violation of the confidentiality obligation as for his own violation.
            </p>
            <p className="mb-2">
              The duty of confidentiality extends indefinitely even beyond the end of this contractual relationship. Exceptions exist in the case of legally provided obligations to testify.
            </p>
            <p>
              The Contractor is entitled to process personal data entrusted to him within the scope of the purpose of the contractual relationship. The Client guarantees the Contractor that all necessary measures, in particular those within the meaning of the Data Protection Act, such as declarations of consent by the persons concerned, have been taken for this purpose.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">9. Remuneration</h3>
            <p className="mb-2">
              After completion of the agreed work, the Contractor receives a fee according to the agreement between the Client and the Contractor. The Contractor is entitled to issue interim invoices according to the progress of the work. The fee is due upon invoicing by the Contractor, unless otherwise agreed.
            </p>
            <p className="mb-2">
              Incurred cash outlays, expenses, travel costs, etc. are to be reimbursed by the Client in addition against invoicing by the Contractor.
            </p>
            <p className="mb-2">
              If the execution of the agreed work is omitted for reasons on the side of the Client, or due to a justified premature termination of the contractual relationship by the Contractor, the Contractor retains the claim to payment of the entire agreed fee minus saved expenses. In the case of an agreement on an hourly fee, the fee for the number of hours expected for the entire agreed work is to be paid minus the saved expenses. The saved expenses are agreed as a lump sum of 30 percent of the fee for those services that the Contractor has not yet performed by the day of the termination of the contractual relationship.
            </p>
            <p>
              In the event of non-payment of interim invoices, the Contractor is released from his obligation to provide further services. However, the assertion of further claims resulting from non-payment is not affected by this.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">10. Reminder Periods</h3>
            <p className="mb-2">In the event of non-payment of fees, the following deadlines and fees are agreed:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>In the event of a default in payment of 4 days (from the due date of the invoice), a payment reminder is sent, for which no additional fees apply.</li>
              <li>In the event of a default in payment of 21 days (from the due date of the invoice), the second reminder is sent, for which flat-rate reminder charges of €40 net apply.</li>
              <li>In the event of a default in payment of 28 days (from the due date of the invoice), the third reminder is sent, for which reminder charges of 5% of the invoice amount net apply.</li>
              <li>In the event of a default in payment of 35 days (from the due date of the invoice), further legal steps will be initiated.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">11. Cancellation Terms</h3>
            <p className="mb-2">After order confirmation, in the event of cancellation, 100% of the travel costs are due as expense allowance as well as</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>2 months before the agreed date 25% of the offer sum.</li>
              <li>1 month before the agreed date 50% of the offer sum.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">12. Electronic Invoicing</h3>
            <p>
              The Contractor is entitled to transmit invoices to the Client also in electronic form. The Client explicitly declares his agreement to the sending of invoices in electronic form by the Contractor.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">13. Warranty</h3>
            <p>
              The conclusion of the contract takes place under exclusion of any warranty. The exclusion does not apply to claims for damages arising from grossly negligent or intentional breach of duties of the seller as well as for any injury to life, body and health.
            </p>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">14. Duration of the Contract</h3>
            <p className="mb-2">
              This contract generally ends with the completion of the project.
            </p>
            <p className="mb-2">
              Regardless of this, the contract can be terminated at any time for important reasons by either side without observing a notice period. An important reason is to be considered in particular,
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>if a contracting partner violates essential contractual obligations or</li>
              <li>if a contracting partner falls into arrears with payment after the opening of insolvency proceedings.</li>
              <li>if there are legitimate concerns about the creditworthiness of a contracting partner against whom no insolvency proceedings have been opened, and this partner neither makes advance payments nor provides suitable security at the request of the Contractor before the Contractor's performance and the poor financial circumstances were not known to the other contracting partner at the conclusion of the contract.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg font-bold text-slate-900 mb-3">15. Final Provisions</h3>
            <p className="mb-2">
              The contracting parties confirm that they have made all statements in the contract conscientiously and truthfully and undertake to announce any changes immediately to each other.
            </p>
            <p className="mb-2">
              Changes to the contract and these GTC require written form; as does a departure from this formal requirement. Oral collateral agreements do not exist.
            </p>
            <p>
              Material Austrian law is applicable to this contract, excluding the reference norms of international private law. The place of performance is Innsbruck, Austria. The regional court of Innsbruck is responsible for disputes.
            </p>
          </section>

          <div className="pt-8 mt-8 border-t border-slate-200 text-sm text-slate-500 font-mono">
            <p>These GTC have been online in the current version since 24.07.2017.</p>
            <p className="break-all mt-2">SHA 256: 41075996525a18740beb3c4300fc612e10ce74da7b28ce3aed3cd1ca6b8325e9</p>
          </div>
        </div>
      </LegalLayout>
    );
  }

  return (
    <LegalLayout title="Allgemeine Geschäftsbedingungen (AGB)" onBack={onBack}>
      <div className="space-y-8 text-slate-700">
        <p className="text-sm text-slate-500 mb-6">(Aktualisiert: 05.12.2017)</p>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">1. Allgemeine Grundlagen / Geltungsbereich</h3>
          <p className="mb-2">
            Für sämtliche Rechtsgeschäfte zwischen dem Auftraggeber und der Build Informed GmbH (in Folge Auftragnehmer genannt) gelten ausschließlich diese Allgemeinen Geschäftsbedingungen. Maßgeblich ist jeweils die zum Zeitpunkt des Vertragsabschlusses gültige Fassung.
          </p>
          <p className="mb-2">
            Diese Allgemeinen Geschäftsbedingungen gelten auch für alle künftigen Vertragsbeziehungen, somit auch dann, wenn bei Zusatzverträgen darauf nicht ausdrücklich hingewiesen wird.
          </p>
          <p className="mb-2">
            Entgegenstehende Allgemeine Geschäftsbedingungen des Auftraggebers sind ungültig, es sei denn, diese werden vom Auftragnehmer ausdrücklich schriftlich anerkannt.
          </p>
          <p>
            Für den Fall, dass einzelne Bestimmungen dieser Allgemeinen Geschäftsbedingungen unwirksam sein und/oder werden sollten, berührt dies die Wirksamkeit der verbleibenden Bestimmungen und der unter ihrer Zugrundelegung geschlossenen Verträge nicht. Die unwirksame ist durch eine wirksame Bestimmung, die ihr dem Sinn und wirtschaftlichen Zweck nach am nächsten kommt, zu ersetzen.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">2. Umfang des Auftrages / Stellvertretung</h3>
          <p className="mb-2">
            Der Umfang eines konkreten Auftrages wird im Einzelfall vertraglich vereinbart.
          </p>
          <p className="mb-2">
            Der Auftragnehmer ist berechtigt, die ihm obliegenden Aufgaben ganz oder teilweise durch Dritte erbringen zu lassen. Die Bezahlung des Dritten erfolgt ausschließlich durch den Auftragnehmer selbst. Es entsteht kein, wie auch immer geartetes, direktes Vertragsverhältnis zwischen dem Dritten und dem Auftraggeber.
          </p>
          <p>
            Der Auftraggeber verpflichtet sich, während sowie bis zum Ablauf von drei Jahren nach Beendigung dieses Vertragsverhältnisses keine wie auch immer geartete Geschäftsbeziehung zu Personen oder Gesellschaften einzugehen, deren sich der Auftragnehmer zur Erfüllung seiner vertraglichen Pflichten bedient. Der Auftraggeber wird diese Personen und Gesellschaften insbesondere nicht mit solchen oder ähnlichen Leistungen beauftragen, die auch der Auftragnehmer anbietet.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">3. Aufklärungspflicht des Auftraggebers / Vollständigkeitserklärung</h3>
          <p className="mb-2">
            Der Auftraggeber sorgt dafür, dass die organisatorischen Rahmenbedingungen bei Erfüllung des Auftrages an seinem Geschäftssitz ein möglichst ungestörtes, dem raschen Fortgang des Arbeitsprozesses förderliches Arbeiten erlauben.
          </p>
          <p className="mb-2">
            Der Auftraggeber wird den Auftragnehmer auch über vorher durchgeführte und/oder laufende Beratungen – auch auf anderen Fachgebieten – umfassend informieren.
          </p>
          <p className="mb-2">
            Der Auftraggeber sorgt dafür, dass dem Auftragnehmer auch ohne dessen besondere Aufforderung alle für die Erfüllung und Ausführung des Beratungsauftrages notwendigen Unterlagen zeitgerecht vorgelegt werden und ihm von allen Vorgängen und Umständen Kenntnis gegeben wird, die für die Ausführung des Beratungsauftrages von Bedeutung sind. Dies gilt auch für alle Unterlagen, Vorgänge und Umstände, die erst während der Tätigkeit des Beraters bekannt werden.
          </p>
          <p>
            Der Auftraggeber sorgt dafür, dass seine Mitarbeiter und die gesetzlich vorgesehene und gegebenenfalls eingerichtete Arbeitnehmervertretung (Betriebsrat) bereits vor Beginn der Tätigkeit des Auftragnehmers von dieser informiert werden.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">4. Sicherung der Unabhängigkeit</h3>
          <p className="mb-2">
            Die Vertragspartner verpflichten sich zur gegenseitigen Loyalität.
          </p>
          <p>
            Die Vertragspartner verpflichten sich gegenseitig, alle Vorkehrungen zu treffen, die geeignet sind, die Gefährdung der Unabhängigkeit der beauftragten Dritten und Mitarbeiter des Auftragnehmers zu verhindern. Dies gilt insbesondere für Angebote des Auftraggebers auf Anstellung bzw. der Übernahme von Aufträgen auf eigene Rechnung.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">5. Berichterstattung / Berichtspflicht</h3>
          <p>
            Der Auftragnehmer ist bei der Herstellung des vereinbarten Werkes weisungsfrei, handelt nach eigenem Gutdünken und in eigener Verantwortung. Er ist an keinen bestimmten Arbeitsort und keine bestimmte Arbeitszeit gebunden.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">6. Schutz des geistigen Eigentums</h3>
          <p className="mb-2">
            Die Urheberrechte an den vom Auftragnehmer und seinen Mitarbeitern und beauftragten Dritten geschaffenen Werke (insbesondere Angebote, Berichte, Analysen, Gutachten, Organisationspläne, Programme, Leistungsbeschreibungen, Entwürfe, Berechnungen, Zeichnungen, Datenträger etc.) verbleiben beim Auftragnehmer. Sie dürfen vom Auftraggeber während und nach Beendigung des Vertragsverhältnisses ausschließlich für vom Vertrag umfasste Zwecke verwendet werden. Der Auftraggeber ist insofern nicht berechtigt, das Werk (die Werke) ohne ausdrückliche Zustimmung des Auftragnehmers zu vervielfältigen und/oder zu verbreiten. Keinesfalls entsteht durch eine unberechtigte Vervielfältigung/Verbreitung des Werkes eine Haftung des Auftragnehmers– insbesondere etwa für die Richtigkeit des Werkes – gegenüber Dritten.
          </p>
          <p>
            Der Verstoß des Auftraggebers gegen diese Bestimmungen berechtigt den Auftragnehmer zur sofortigen vorzeitigen Beendigung des Vertragsverhältnisses und zur Geltendmachung anderer gesetzlicher Ansprüche, insbesondere auf Unterlassung und/oder Schadenersatz.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">7. Haftung / Schadenersatz</h3>
          <p className="mb-2">
            Der Auftragnehmer haftet dem Auftraggeber für Schäden – ausgenommen für Personenschäden – nur im Falle groben Verschuldens (Vorsatz oder grobe Fahrlässigkeit). Dies gilt sinngemäß auch für Schäden, die auf vom Auftragnehmer beigezogene Dritte zurückgehen.
          </p>
          <p className="mb-2">
            Schadenersatzansprüche des Aufraggebers können nur innerhalb von sechs Monaten ab Kenntnis von Schaden und Schädiger, spätestens aber innerhalb von drei Jahren nach dem anspruchsbegründenden Ereignis gerichtlich geltend gemacht werden.
          </p>
          <p className="mb-2">
            Der Auftraggeber hat jeweils den Beweis zu erbringen, dass der Schaden auf ein Verschulden des Auftragnehmers zurückzuführen ist.
          </p>
          <p>
            Sofern der Auftragnehmer das Werk unter Zuhilfenahme Dritter erbringt und in diesem Zusammenhang Gewährleistungs- und/oder Haftungsansprüche gegenüber diesen Dritten entstehen, tritt der Auftragnehmer diese Ansprüche an den Auftraggeber ab. Der Auftraggeber wird sich in diesem Fall vorrangig an diese Dritten halten.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">8. Geheimhaltung / Datenschutz</h3>
          <p className="mb-2">
            Der Auftragnehmer verpflichtet sich zu Stillschweigen über Geschäfts- und Betriebsgeheimnisse des Auftraggebers.
          </p>
          <p className="mb-2">
            Der Auftragnehmer ist von der Schweigepflicht gegenüber allfälligen Gehilfen und Stellvertretern, denen er sich bedient, entbunden. Er hat die Schweigepflicht aber auf diese vollständig zu überbinden und haftet für deren Verstoß gegen die Verschwiegenheitsverpflichtung wie für einen eigenen Verstoß.
          </p>
          <p className="mb-2">
            Die Schweigepflicht reicht unbegrenzt auch über das Ende dieses Vertragsverhältnisses hinaus. Ausnahmen bestehen im Falle gesetzlich vorgesehener Aussageverpflichtungen.
          </p>
          <p>
            Der Auftragnehmer ist berechtigt, ihm anvertraute personenbezogene Daten im Rahmen der Zweckbestimmung des Vertragsverhältnisses zu verarbeiten. Der Auftraggeber leistet dem Auftragnehmer Gewähr, dass hierfür sämtliche erforderlichen Maßnahmen insbesondere jene im Sinne des Datenschutzgesetzes, wie etwa Zustimmungserklärungen der Betroffenen, getroffen worden sind.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">9. Honorar</h3>
          <p className="mb-2">
            Nach Vollendung des vereinbarten Werkes erhält der Auftragnehmer ein Honorar gemäß der Vereinbarung zwischen dem Auftraggeber und dem Auftragnehmer. Der Auftragnehmer ist berechtigt, dem Arbeitsfortschritt entsprechend Zwischenabrechnungen zu stellen. Das Honorar ist jeweils, falls nicht anders vereinbart, mit Rechnungslegung durch den Auftragnehmer fällig.
          </p>
          <p className="mb-2">
            Anfallende Barauslagen, Spesen, Reisekosten, etc. sind gegen Rechnungslegung des Auftragnehmers vom Auftraggeber zusätzlich zu ersetzen.
          </p>
          <p className="mb-2">
            Unterbleibt die Ausführung des vereinbarten Werkes aus Gründen, die aufseiten des Auftraggebers liegen, oder aufgrund einer berechtigten vorzeitigen Beendigung des Vertragsverhältnisses durch den Auftragnehmer, so behält der Auftragnehmer den Anspruch auf Zahlung des gesamten vereinbarten Honorars abzüglich ersparter Aufwendungen. Im Falle der Vereinbarung eines Stundenhonorars ist das Honorar für jene Stundenanzahl, die für das gesamte vereinbarte Werk zu erwarten gewesen ist, abzüglich der ersparten Aufwendungen zu leisten. Die ersparten Aufwendungen sind mit 30 Prozent des Honorars für jene Leistungen, die der Auftragnehmer bis zum Tage der Beendigung des Vertragsverhältnisses noch nicht erbracht hat, pauschaliert vereinbart.
          </p>
          <p>
            Im Falle der Nichtzahlung von Zwischenabrechnungen ist der Auftragnehmer von seiner Verpflichtung, weitere Leistungen zu erbringen, befreit. Die Geltendmachung weiterer aus der Nichtzahlung resultierender Ansprüche wird dadurch aber nicht berührt.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">10. Mahnfristen</h3>
          <p className="mb-2">Im Falle der Nichtzahlung von Honoraren werden folgende Firsten und Gebühren vereinbart:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Bei einem Zahlungsverzug von 4 Tagen (ab Fälligkeit der Rechnung) wird eine Zahlungserinnerung verschickt, für die keine zusätzlichen Gebühren anfallen</li>
            <li>Bei einem Zahlungsverzug von 21 Tagen (ab Fälligkeit der Rechnung) wird die zweite Mahnung verschickt, für die pauschalierte Mahnspesen von 40€ netto anfallen</li>
            <li>Bei einem Zahlungsverzug von 28 Tagen (ab Fälligkeit der Rechnung) wird die dritte Mahnung verschickt, für die Mahnspesen von 5% der Rechnungssumme netto anfallen.</li>
            <li>Bei einem Zahlungsverzug von 35 Tagen (ab Fälligkeit der Rechnung) werden weitere rechtliche Schritte eingeleitet.</li>
          </ul>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">11. Stornobedingungen</h3>
          <p className="mb-2">Nach Angebotsbestätigung sind im Fall einer Stornierung 100% der Reisekosten als Aufwandsentschädigung fällig sowie</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>2 Monate vor dem vereinbarten Termin 25% der Angebotssumme.</li>
            <li>1 Monat vor dem vereinbarten Termin 50% der Angebotssumme.</li>
          </ul>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">11. Elektronische Rechnungslegung (sic)</h3>
          <p>
            Der Auftragnehmer ist berechtigt, dem Auftraggeber Rechnungen auch in elektronischer Form zu übermitteln. Der Auftraggeber erklärt sich mit der Zusendung von Rechnungen in elektronischer Form durch den Auftragnehmer ausdrücklich einverstanden.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">12. Gewährleistung</h3>
          <p>
            Der Vertragsabschluss erfolgt unter Ausschluss jeglicher Gewährleistung. Der Ausschluss gilt nicht für Schadenersatzansprüche aus grob fahrlässiger bzw. vorsätzlicher Verletzung von Pflichten des Verkäufers sowie für jede Verletzung von Leben, Körper und Gesundheit.
          </p>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">13. Dauer des Vertrages</h3>
          <p className="mb-2">
            Dieser Vertrag endet grundsätzlich mit dem Abschluss des Projekts.
          </p>
          <p className="mb-2">
            Der Vertrag kann dessen ungeachtet jederzeit aus wichtigen Gründen von jeder Seite ohne Einhaltung einer Kündigungsfrist gelöst werden. Als wichtiger Grund ist insbesondere anzusehen,
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>wenn ein Vertragspartner wesentliche Vertragsverpflichtungen verletzt oder</li>
            <li>wenn ein Vertragspartner nach Eröffnung eines Insolvenzverfahrens in Zahlungsverzug gerät.</li>
            <li>wenn berechtigte Bedenken hinsichtlich der Bonität eines Vertragspartners, über den kein Insolvenzverfahren eröffnet ist, bestehen und dieser auf Begehren des Auftragnehmers weder Vorauszahlungen leistet noch vor Leistung des Auftragnehmers eine taugliche Sicherheit leistet und die schlechten Vermögensverhältnisse dem anderen Vertragspartner bei Vertragsabschluss nicht bekannt waren.</li>
          </ul>
        </section>

        <section>
          <h3 className="text-lg font-bold text-slate-900 mb-3">14. Schlussbestimmungen</h3>
          <p className="mb-2">
            Die Vertragsparteien bestätigen, alle Angaben im Vertrag gewissenhaft und wahrheitsgetreu gemacht zu haben und verpflichten sich, allfällige Änderungen wechselseitig umgehend bekannt zu geben.
          </p>
          <p className="mb-2">
            Änderungen des Vertrages und dieser AGB bedürfen der Schriftform; ebenso ein Abgehen von diesem Formerfordernis. Mündliche Nebenabreden bestehen nicht.
          </p>
          <p>
            Auf diesen Vertrag ist materielles österreichisches Recht unter Ausschluss der Verweisungsnormen des internationalen Privatrechts anwendbar. Erfüllungsort ist Innsbruck, Österreich. Für Streitigkeiten ist das Landesgericht Innsbruck zuständig.
          </p>
        </section>

        <div className="pt-8 mt-8 border-t border-slate-200 text-sm text-slate-500 font-mono">
          <p>Diese AGB´s sind seit dem 24.07.2017 in der derzeitigen Fassung online.</p>
          <p className="break-all mt-2">SHA 256: 41075996525a18740beb3c4300fc612e10ce74da7b28ce3aed3cd1ca6b8325e9</p>
        </div>
      </div>
    </LegalLayout>
  );
}
